import React, { useState } from 'react';
import { usePOSStore } from '../../../stores/pos.store';
import { useServices, useServiceCategories, usePackages, useBaseCurrency, useStaff } from '../../../db/hooks/useLookupData';
import { Search, Clock, Tag, Sparkles, Package } from 'lucide-react';
import { POSOperatorPickerModal } from '../POSOperatorPickerModal';
import type { ServiceDocType, PriceTier, PackageDocType } from '../../../db/schemas';

export function POSServiceSelector() {
  const {
    cartItems,
    setIsSelectingService,
    addServiceToCart,
    addPackageToCart,
    defaultOperatorId,
    setDefaultOperatorId,
    openStaffCreation,
  } = usePOSStore();
  const { services = [] } = useServices();
  const { serviceCategories = [] } = useServiceCategories();
  const { packages = [] } = usePackages();
  const { staff = [] } = useStaff();
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  
  const [activeTab, setActiveTab] = useState<'services' | 'packages'>('services');
  const [searchTerm, setSearchTerm] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<{
    type: 'service' | 'package';
    service?: ServiceDocType;
    package?: PackageDocType;
    tier?: PriceTier | null;
  } | null>(null);

  // Auto-select operator if only 1 exists and none selected yet
  const getActiveOperatorId = () => {
    if (defaultOperatorId) return defaultOperatorId;
    if (staff.length === 1) {
      setDefaultOperatorId(staff[0].id);
      return staff[0].id;
    }
    return null;
  };

  // Filtrado de services
  const filteredServices = services.filter(s => 
    s.concept.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Agrupamiento por categorías
  const catIdsSet = new Set(serviceCategories.map(c => c.id));
  const knownGrouped = serviceCategories.map(cat => ({
    id: cat.id,
    name: cat.name,
    services: filteredServices.filter(s => s.category_id === cat.id)
  })).filter(g => g.services.length > 0);

  const orphanServices = filteredServices.filter(s => !s.category_id || !catIdsSet.has(s.category_id));
  
  const grouped = [
    ...knownGrouped,
    ...(orphanServices.length > 0 ? [{
      id: 'uncategorized',
      name: 'General / Otros',
      services: orphanServices
    }] : [])
  ];

  // Filtrado de packages
  const filteredPackages = packages.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelectService = (service: ServiceDocType, tier?: PriceTier | null) => {
    const operatorId = getActiveOperatorId();
    if (!operatorId) {
      setPendingSelection({ type: 'service', service, tier });
      setIsPickerOpen(true);
    } else {
      addServiceToCart(service, operatorId, tier);
    }
  };

  const handleSelectPackage = (pkg: PackageDocType) => {
    const operatorId = getActiveOperatorId();
    if (!operatorId) {
      setPendingSelection({ type: 'package', package: pkg });
      setIsPickerOpen(true);
    } else {
      addPackageToCart(pkg, operatorId);
    }
  };

  const handleConfirmOperator = (operatorId: string) => {
    setDefaultOperatorId(operatorId);
    if (pendingSelection) {
      if (pendingSelection.type === 'service' && pendingSelection.service) {
        addServiceToCart(pendingSelection.service, operatorId, pendingSelection.tier);
      } else if (pendingSelection.type === 'package' && pendingSelection.package) {
        addPackageToCart(pendingSelection.package, operatorId);
      }
      setPendingSelection(null);
    }
  };

  const handleAddNewStaff = () => {
    setIsPickerOpen(false);
    openStaffCreation('default', pendingSelection);
  };

  return (
    <div className="flex flex-col h-full animate-fade-in bg-surface overflow-hidden">
      
      {/* Top Header & Operator Chip */}
      <div className="p-3.5 sm:p-6 pb-2 flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Tabs Selector */}
        <div className="flex bg-surface-secondary border border-border p-1 rounded-xl mb-3 sm:mb-4 shrink-0">
          <button
            onClick={() => setActiveTab('services')}
            className={`flex-1 py-2 px-2 sm:px-3 text-xs font-bold whitespace-nowrap rounded-lg transition-all flex items-center justify-center gap-1.5 sm:gap-2 cursor-pointer ${
              activeTab === 'services'
                ? 'bg-surface text-brand-sky shadow-xs border border-border/40'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 shrink-0" />
            <span>Servicios ({services.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('packages')}
            className={`flex-1 py-2 px-2 sm:px-3 text-xs font-bold whitespace-nowrap rounded-lg transition-all flex items-center justify-center gap-1.5 sm:gap-2 cursor-pointer ${
              activeTab === 'packages'
                ? 'bg-surface text-brand-sky shadow-xs border border-border/40'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <Package className="w-3.5 h-3.5 shrink-0" />
            <span>Paquetes / Combos ({packages.length})</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4 shrink-0">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={activeTab === 'services' ? "Buscar por servicio..." : "Buscar por paquete o combo..."}
            className="w-full bg-surface-secondary border border-border rounded-xl py-2.5 pl-10 pr-4 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky outline-none transition-all"
          />
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 space-y-6 pb-4">
          {activeTab === 'services' ? (
            grouped.length === 0 ? (
              <div className="p-8 text-center bg-surface-secondary/50 rounded-2xl border border-dashed border-border">
                <p className="text-sm font-bold text-text-secondary">No se encontraron services</p>
              </div>
            ) : (
              grouped.map(group => (
                <div key={group.id}>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="font-extrabold text-base text-text-primary">{group.name}</h3>
                    <span className="bg-surface-secondary text-text-secondary text-[10px] font-bold px-2 py-0.5 rounded-full border border-border">
                      {group.services.length}
                    </span>
                  </div>
                  
                  <div className="space-y-2.5">
                    {group.services.map(srv => {
                      const tieneEscalones = Array.isArray(srv.price_tiers) && srv.price_tiers.length > 0;
                      return (
                        <div 
                          key={srv.id}
                          className="p-3.5 rounded-2xl bg-surface-secondary/60 hover:bg-brand-sky/5 border border-border hover:border-brand-sky/30 transition-all"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="border-l-2 border-brand-sky pl-3">
                              <span className="font-bold text-sm text-text-primary block">{srv.concept}</span>
                              <span className="text-xs text-text-secondary mt-0.5 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {srv.duration_minutes || 30} min
                              </span>
                            </div>
                            
                            <button
                              onClick={() => handleSelectService(srv, null)}
                              className="bg-surface hover:bg-brand-sky hover:text-white text-text-primary border border-border px-3 py-1.5 rounded-xl font-extrabold text-xs transition-all shadow-xs cursor-pointer flex items-center gap-1.5"
                            >
                              <span>+ Añadir</span>
                              <span className="text-brand-sky hover:text-white font-black">1x • {currencySymbol}{srv.currency_price}</span>
                            </button>
                          </div>

                          {tieneEscalones && (
                            <div className="mt-2.5 pt-2.5 border-t border-border/60 flex flex-wrap items-center gap-2">
                              <span className="text-[10px] font-bold text-text-tertiary uppercase tracking-wider flex items-center gap-1">
                                <Tag className="w-3 h-3 text-brand-sky" /> Oferta volumen:
                              </span>
                              {(srv.price_tiers || []).map((esc, idx) => (
                                <button
                                  key={idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectService(srv, esc);
                                  }}
                                  className="bg-brand-sky/10 hover:bg-brand-sky hover:text-white text-brand-sky border border-brand-sky/20 px-2.5 py-1 rounded-lg font-extrabold text-xs transition-all cursor-pointer"
                                >
                                  + {esc.label} • {currencySymbol}{esc.total_price} {baseCurrency}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )
          ) : (
            filteredPackages.length === 0 ? (
              <div className="p-8 text-center bg-surface-secondary/50 rounded-2xl border border-dashed border-border">
                <p className="text-sm font-bold text-text-secondary">No hay packages activos en el catálogo</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredPackages.map(pkg => (
                  <div
                    key={pkg.id}
                    onClick={() => handleSelectPackage(pkg)}
                    className="group p-4 rounded-2xl bg-surface-secondary/60 hover:bg-brand-sky/5 border border-border hover:border-brand-sky/30 transition-all cursor-pointer"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-extrabold text-base text-text-primary group-hover:text-brand-sky transition-colors">
                          {pkg.name}
                        </span>
                        {pkg.description && (
                          <p className="text-xs text-text-secondary mt-1">{pkg.description}</p>
                        )}
                      </div>
                      <span className="font-black text-base text-brand-sky bg-brand-sky/10 border border-brand-sky/20 px-3 py-1 rounded-xl shrink-0">
                        {currencySymbol}{pkg.currency_price} {baseCurrency}
                      </span>
                    </div>

                    {pkg.items && pkg.items.length > 0 && (
                      <div className="mt-3 pt-2.5 border-t border-border/60 flex flex-wrap gap-1.5">
                        {pkg.items.map((it: any, i: number) => {
                          const srv = services.find(s => s.id === it.service_id);
                          return (
                            <span 
                              key={i} 
                              className="bg-surface text-text-secondary text-[10px] font-bold px-2 py-0.5 rounded-md border border-border"
                            >
                              {it.session_count}x {srv?.concept || 'Servicio'}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* Operator Selection Modal */}
      <POSOperatorPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelectOperator={handleConfirmOperator}
        onAddNewStaff={handleAddNewStaff}
      />
    </div>
  );
}
